import { Card, CardContent, CardHeader, CardTitle, useStepperContext } from '@folio/components';
import { useAssertedParams } from '@folio/lib';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  brokerageFirmControllerGenerateSignatureRequest,
  distributionRequestStatusEnum,
  viewSpecificControllerListBrokerageFirmDistributionRequestsQueryOptions,
  policyControllerGeneratePolicySignatureRequest,
  signatureControllerGetSignatureRequestsByExternalId,
  signatureControllerGetSignerLink,
  signatureRequestDeliveryModeEnum,
  signatureRequestStatusEnum,
} from 'korint-sdk';
import _ from 'lodash';
import { Loader2Icon } from 'lucide-react';
import { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@folio/components/ui/flex';

declare global {
  class Yousign {
    constructor(opts: { signatureLink: string; iframeContainerId: string; isSandbox?: boolean });
    onSuccess(onSuccess: () => void): void;
    onError(onError: () => void): void;
    onStarted(onStarted: () => void): void;
  }

  interface Window {
    Yousign: typeof Yousign;
  }
}

export interface IntermediarySignatureFunnelModuleProps {
  displayContext: 'broker' | 'policyholder';
}

function SignatureFallback() {
  const { t } = useTranslation();
  return (
    <Stack align="center" justify="center" className="top-0 left-0 bottom-0 right-0 py-32">
      <Loader2Icon className="w-8 h-8 animate-spin" />
      <div className="text-sm text-muted-foreground">
        {t('intermediarySignatureFunnel.loading')}
      </div>
    </Stack>
  );
}

function BrokerSignature() {
  const { brokerageFirmId } = useAssertedParams();
  const stepper = useStepperContext();

  const { data: currentDistributionRequests } = useSuspenseQuery({
    ...viewSpecificControllerListBrokerageFirmDistributionRequestsQueryOptions(brokerageFirmId),
  });

  const hasApprovedDistributionRequest = currentDistributionRequests?.data.some(
    distributionRequest => distributionRequest.status === distributionRequestStatusEnum.APPROVED,
  );

  const {
    data: signatureData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['getBrokerageFirmSignatureLink', brokerageFirmId],
    queryFn: async () => {
      const data = await brokerageFirmControllerGenerateSignatureRequest(brokerageFirmId, {
        deliveryMode: 'URL',
      });
      return { signatureLink: data.signatures[0]?.signers[0]?.url };
    },
    retry: (failureCount: number, error: { status: number }) =>
      error.status === 425 && failureCount < 100,
    retryDelay: () => 1000,
    enabled: !hasApprovedDistributionRequest,
  });

  // Derive signature completion state
  const isSignatureComplete = hasApprovedDistributionRequest || error?.status === 409;
  const signatureLink = signatureData?.signatureLink;

  useEffect(() => {
    if (isSignatureComplete) {
      stepper.next();
      return;
    }
  }, [isSignatureComplete, stepper]);

  // Handle navigation and Yousign initialization in a single effect
  useEffect(() => {
    if (signatureLink) {
      const yousign = new window.Yousign({
        signatureLink,
        iframeContainerId: 'yousign',
        isSandbox: new URL(signatureLink).searchParams.get('sandbox') === 'true',
      });
      yousign.onSuccess(() => stepper.next());
    }
  }, [signatureLink, stepper]);

  return isLoading ? <SignatureFallback /> : <div id="yousign" className="*:h-[700px] *:w-full" />;
}

function PolicyholderSignature() {
  const { policyId, branchId } = useAssertedParams();
  const stepper = useStepperContext();
  const {
    data: { signatureLink, hasSigned },
  } = useSuspenseQuery({
    queryKey: ['getGeneratedSignatureLink', policyId, branchId],
    queryFn: async () => {
      // reuse the pending request SendContractButton created; create is not idempotent
      async function getSignatureLink() {
        const { signatureRequests } =
          await signatureControllerGetSignatureRequestsByExternalId(policyId);

        // by-external-id is policy-wide (externalId = policyId); scope to this branch
        const branchRequests = signatureRequests.filter(
          ({ metadata }) => metadata.find(({ key }) => key === 'branchId')?.value === branchId,
        );

        // only an embedded (NONE) request signs in this iframe; newest wins
        const pendingRequest = _.maxBy(
          branchRequests.filter(
            ({ status, deliveryMode }) =>
              status === signatureRequestStatusEnum.PENDING &&
              deliveryMode === signatureRequestDeliveryModeEnum.NONE,
          ),
          request => request.createdAt ?? '',
        );
        const signerCustomerId = pendingRequest?.signers[0]?.customerId;

        // a signed request wins: never reopen a pending one on an already-signed branch
        if (branchRequests.some(({ status }) => status === signatureRequestStatusEnum.SIGNED)) {
          return { signatureLink: undefined, hasSigned: true };
        }

        if (pendingRequest && signerCustomerId) {
          const { url } = await signatureControllerGetSignerLink(
            policyId,
            pendingRequest.id,
            signerCustomerId,
          );
          return { signatureLink: url, hasSigned: false };
        }

        // no prior request: self-service reaches this step before the button creates one
        const data = await policyControllerGeneratePolicySignatureRequest(
          policyId,
          { deliveryMode: 'URL' },
          { branchId },
        );
        return { signatureLink: data.signatures[0]?.signers[0]?.url, hasSigned: false };
      }

      let retries = 0;
      while (retries < 100) {
        try {
          return await getSignatureLink();
        } catch (error) {
          const statusCode = _.get(error, 'errorResponse.status', 500) as number;
          const errorCode = _.get(error, 'errorResponse.data.code');

          if (errorCode === 'CONFLICTING_BRANCH_STATUS') {
            return { signatureLink: undefined, hasSigned: true };
          }

          if (statusCode !== 425 && retries >= 10) {
            break;
          }

          retries++;
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      return { signatureLink: undefined, hasSigned: false };
    },
  });

  // Not working if inside the useSuspenseQuery, so we need to do it here
  useEffect(() => {
    if (hasSigned) {
      stepper.next();
    }
  }, [hasSigned, stepper]);

  // Need to first load the iframe with id 'yousign' before initializing Yousign
  useEffect(() => {
    if (signatureLink) {
      const yousign = new window.Yousign({
        signatureLink,
        iframeContainerId: 'yousign',
        isSandbox: new URL(signatureLink).searchParams.get('sandbox') === 'true',
      });
      yousign.onSuccess(() => stepper.next());
    }
  }, [signatureLink, stepper]);

  return !hasSigned && <div id="yousign" className="*:h-[700px] *:w-full" />;
}

export function IntermediarySignatureFunnelModule({
  displayContext,
}: IntermediarySignatureFunnelModuleProps) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('intermediarySignatureFunnel.title')}</CardTitle>
      </CardHeader>
      <CardContent className="py-4">
        <Suspense fallback={<SignatureFallback />}>
          {displayContext === 'broker' ? <BrokerSignature /> : <PolicyholderSignature />}
        </Suspense>
      </CardContent>
    </Card>
  );
}
